import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { useClock } from '@/shared/hooks/useClock';
import { formatTimeFull } from '@/shared/utils/datetime';
import { db } from '@/shared/db/database';

type LocationState = {
  conflictingEmployee?: {
    idPerson?: number;
    fullName?: string;
  };
} | null;

export function EnrollDuplicateFaceErrorPage() {
  const { t } = useTranslation('common');
  const { C, T } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const now = useClock();

  const state = location.state as LocationState;
  const fromState = state?.conflictingEmployee;

  const [conflictingName, setConflictingName] = useState<string | null>(
    fromState?.fullName ?? null,
  );

  // Fallback: if name not in state, look up from db.employees
  useEffect(() => {
    if (conflictingName) return;
    if (!fromState?.idPerson) return;

    void db.employees.get(fromState.idPerson).then((empRow) => {
      if (empRow) {
        const raw = (empRow.raw ?? {}) as Record<string, unknown>;
        const full = `${String(raw['first_name'] ?? '')} ${String(raw['last_name1'] ?? '')}`.trim();
        setConflictingName(full || String(fromState.idPerson));
      } else {
        setConflictingName(String(fromState.idPerson));
      }
    });
  }, [conflictingName, fromState]);

  const displayName = conflictingName ?? (fromState?.idPerson ? String(fromState.idPerson) : '—');
  const conflictLabel = t('admin.enrollDuplicateFaceConflict', { name: displayName });

  return (
    <div
      style={{
        flex: 1,
        background: T.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '30px',
      }}
    >
      <div
        className="mi-anim-slide-up"
        style={{
          flex: 1,
          width: '100%',
          maxWidth: 407,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Danger X icon — identical layout to MarkingErrorPage */}
        <div className="mi-anim-check-pop" style={{ marginBottom: 23 }}>
          <div
            style={{
              width: 99,
              height: 99,
              borderRadius: '50%',
              background: `${C.danger}18`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                width: 75,
                height: 75,
                borderRadius: '50%',
                background: C.danger,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg
                width={33}
                height={33}
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </div>
          </div>
        </div>

        {/* Title */}
        <div
          style={{
            fontSize: 23,
            fontWeight: 700,
            color: T.clockText,
            marginBottom: 7,
            textAlign: 'center',
          }}
        >
          {t('admin.enrollDuplicateFaceTitle')}
        </div>

        {/* Description */}
        <p
          style={{
            fontSize: 12,
            color: T.textMuted,
            textAlign: 'center',
            lineHeight: 1.7,
            marginBottom: 7,
            maxWidth: 330,
          }}
        >
          {t('admin.enrollDuplicateFaceMessage')}
          <br />
          {conflictLabel}
        </p>

        {/* Hint */}
        <p
          style={{
            fontSize: 11,
            color: T.textMuted,
            textAlign: 'center',
            lineHeight: 1.6,
            marginBottom: 7,
            maxWidth: 330,
          }}
        >
          {t('admin.enrollDuplicateFaceHint')}
        </p>

        {/* Current time */}
        <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 30 }}>
          {formatTimeFull(now)}
        </div>

        {/* No CountdownBar — admin must decide */}

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', width: '100%', maxWidth: 300 }}>
          <button
            onClick={() => { void navigate(-2); }}
            style={{
              width: '100%',
              padding: '11px 28px',
              borderRadius: 9,
              border: 'none',
              background: C.primary,
              color: '#fff',
              fontFamily: 'inherit',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 7,
            }}
          >
            <svg
              width={13}
              height={13}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            {t('admin.enrollDuplicateFaceBack')}
          </button>

          <button
            onClick={() => { void navigate(-1); }}
            style={{
              width: '100%',
              padding: '10px 28px',
              borderRadius: 9,
              border: `1px solid ${C.blue}40`,
              background: `${C.blue}12`,
              color: C.blue,
              fontFamily: 'inherit',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 7,
            }}
          >
            <svg
              width={13}
              height={13}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
            >
              <path d="M3 12a9 9 0 1 0 3-6.7" />
              <path d="M3 4v5h5" />
            </svg>
            {t('admin.enrollDuplicateFaceRetry')}
          </button>
        </div>
      </div>
    </div>
  );
}


